import React, { FC, useState, useRef, useEffect, useCallback } from 'react'
import { PopoverProps } from './Popover.types'
import './Popover.scss'

/**
 * Adel Popover
 * https://digital.qa.ameriprise.com/adel/components/popover/popover.hbs?theme=amp.next
 */
export const Popover: FC<PopoverProps> = props => {
  const { items, currentDisplayIndex, hiddenMessage, testId } = props
  const displayLabel = items[Number(currentDisplayIndex)].text
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const popoverRef = useRef<HTMLButtonElement>(null)
  const linkRefs = useRef<Array<HTMLAnchorElement | null>>([])
  const [currentFocusIndex, setCurrentFocusIndex] = useState<number>(0)

  const handleWindowClick = useCallback(
    (e: { target: any }) => {
      if (!popoverRef.current || popoverRef.current.contains(e.target) || !isOpen) {
        return
      }
      setIsOpen(false)
    },
    [isOpen]
  )

  useEffect(() => {
    window.addEventListener('click', handleWindowClick)
    return () => {
      window.addEventListener('click', handleWindowClick)
    }
  }, [handleWindowClick])

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
    if (
      e.key === 'Tab' &&
      linkRefs.current.indexOf(document.activeElement as HTMLAnchorElement) >= 0
    ) {
      if (isOpen && !e.shiftKey) {
        setCurrentFocusIndex(currentFocusIndex + 1)
      } else if (isOpen && e.shiftKey) {
        setCurrentFocusIndex(currentFocusIndex ? currentFocusIndex - 1 : currentFocusIndex)
      }
    }
    if (e.key === 'ArrowDown') {
      setIsOpen(true)
      if (
        linkRefs.current[currentFocusIndex] === document.activeElement &&
        currentFocusIndex < linkRefs.current.length - 1
      ) {
        linkRefs.current[currentFocusIndex + 1]?.focus()
        setCurrentFocusIndex(currentFocusIndex + 1)
      } else if (
        isOpen &&
        document.activeElement === linkRefs.current[linkRefs.current.length - 1]
      ) {
        setIsOpen(false)
        setCurrentFocusIndex(0)
      }
    }
    if (e.key === 'ArrowUp') {
      if (
        isOpen &&
        document.activeElement === linkRefs.current[currentFocusIndex] &&
        currentFocusIndex > 0
      ) {
        linkRefs.current[currentFocusIndex - 1]?.focus()
        setCurrentFocusIndex(currentFocusIndex - 1)
      } else if (isOpen && document.activeElement === linkRefs.current[0]) {
        popoverRef.current?.focus()
        setIsOpen(false)
        setCurrentFocusIndex(0)
      }
    }
  }
  useEffect(() => {
    if (isOpen) {
      linkRefs.current[+currentFocusIndex]?.focus()
    }
  }, [isOpen])

  return (
    <button
      className='u-block icon-globe u-md-hidden u-sm-hidden Link u-linkClean u-flex u-flexAlignItemsCenter u-posRelative'
      ref={popoverRef}
      onClick={() => {
        setIsOpen(() => !isOpen)
      }}
      onKeyDown={onKeyDown}
      aria-haspopup='true'
      aria-expanded={isOpen}
      data-testid={testId ?? null}>
      <div className='u-flex u-flexAlignItemsCenter u-flexJustifyBetween'>
        <span
          aria-hidden='true'
          className='adel-material-symbols-20 u-textColorInherit u-textSizeMsrp2'>
          language
        </span>
        <span className='u-marginLeft4'>{displayLabel}</span>
        <span
          aria-hidden='true'
          className='adel-material-symbols-20 u-textColorInherit u-textSizeMsrp2'>
          keyboard_arrow_down
        </span>
      </div>
      <span className='u-hiddenVisually'>{hiddenMessage}</span>
      {isOpen && (
        <div className='Popover Popover--Compound Popover--Custom u-md-hidden u-sm-hidden is-visible'>
          <ul className='List List--selectable'>
            {items?.map((item, index) => {
              return (
                <li className='List-item u-padding0' key={item.text}>
                  <a
                    className='Link u-paddingVert8 u-paddingHoriz16 u-block u-sizeFull u-linkClean'
                    href={item.href}
                    ref={el => {
                      if (linkRefs.current) linkRefs.current[index] = el
                    }}
                    data-analytics={item.analytics}
                    data-tracking='Link'>
                    {item.text}
                  </a>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </button>
  )
}
